import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { WeatherData } from '@/types/weather';
import html2canvas from 'html2canvas';

export const getWeatherLabel = (condition: string): string => {
  switch (condition) {
    case 'clear': return 'Clear';
    case 'partlyCloudy': return 'Partly Cloudy';
    case 'cloudy': return 'Cloudy';
    case 'rain': return 'Rain';
    case 'heavyRain': return 'Heavy Rain';
    case 'thunderstorm': return 'Thunderstorm';
    case 'snow': return 'Snow';
    case 'fog': return 'Fog';
    case 'wind': return 'Wind';
    case 'alert': return 'Weather Alert';
    default: return 'Cloudy';
  }
};

export const getSharingMessage = (weatherData: WeatherData): string => {
  const weatherInfo = `Weather for ${weatherData.location} on ${format(weatherData.date, "EEEE d MMMM yyyy", { locale: enUS })}: ${getWeatherLabel(weatherData.condition)}, ${weatherData.temperature}°C`;
  return `Knock knock... The weather you wish for is knocking at your door... ${weatherInfo}`;
};

export const generateWeatherImage = async (imageRef: React.RefObject<HTMLDivElement>): Promise<string | null> => {
  if (!imageRef.current) return null;
  
  try {
    const canvas = await html2canvas(imageRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null
    });
    
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating image for sharing:', error);
    return null;
  }
};

export const shareViaWebShare = async (weatherData: WeatherData, imageRef: React.RefObject<HTMLDivElement>): Promise<boolean> => {
  if (!navigator.share) return false;
  
  try {
    const imageUrl = await generateWeatherImage(imageRef);
    const shareData: ShareData = {
      title: `Weather for ${weatherData.location}`,
      text: getSharingMessage(weatherData),
    };
    
    if (imageUrl) {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], `weather-${weatherData.location}.png`, { type: 'image/png' });
      shareData.files = [file];
    }
    
    await navigator.share(shareData);
    return true;
  } catch (error) {
    console.error('Error sharing with Web Share API:', error);
    return false;
  }
};

export const shareViaEmail = async (weatherData: WeatherData, imageRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  const subject = encodeURIComponent(`Weather forecast for ${weatherData.location}`);
  const body = encodeURIComponent(getSharingMessage(weatherData));
  
  const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  
  try {
    const imageUrl = await generateWeatherImage(imageRef);
    if (imageUrl) {
      const downloadLink = document.createElement('a');
      downloadLink.href = imageUrl;
      downloadLink.download = `meteo-${weatherData.location}-${format(weatherData.date, 'yyyy-MM-dd')}.png`;
      downloadLink.click();
      
      setTimeout(() => {
        window.open(mailtoLink);
      }, 500);
    } else {
      window.open(mailtoLink);
    }
  } catch (error) {
    console.error('Error preparing email share:', error);
    window.open(mailtoLink);
  }
};

export const shareViaWhatsApp = async (weatherData: WeatherData, imageRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  try {
    const shared = await shareViaWebShare(weatherData, imageRef);
    if (shared) return;
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`https://wa.me/?text=${text}`);
  } catch (error) {
    console.error('Error sharing on WhatsApp:', error);
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`https://wa.me/?text=${text}`);
  }
};

export const shareViaTelegram = async (weatherData: WeatherData, imageRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  try {
    const shared = await shareViaWebShare(weatherData, imageRef);
    if (shared) return;
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`https://t.me/share/url?url=&text=${text}`);
  } catch (error) {
    console.error('Error sharing on Telegram:', error);
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`https://t.me/share/url?url=&text=${text}`);
  }
};

export const shareViaSMS = async (weatherData: WeatherData, imageRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  try {
    const shared = await shareViaWebShare(weatherData, imageRef);
    if (shared) return;
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`sms:?&body=${text}`);
  } catch (error) {
    console.error('Error sharing via SMS:', error);
    
    const text = encodeURIComponent(getSharingMessage(weatherData));
    window.open(`sms:?&body=${text}`);
  }
};
